import React, { useState, useMemo } from 'react';
import { songData } from '../data/songs';
import { Song } from '../types';
import { getEmbedUrl } from '../utils/youtubeUtils';
import '../styles/components/Songlist.css';

const Songlist: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedGenre, setSelectedGenre] = useState<string>('all');
  const [selectedDecade, setSelectedDecade] = useState<string>('all');
  const [selectedVideo, setSelectedVideo] = useState<Song | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [displayedSongs, setDisplayedSongs] = useState<Song[]>(songData);
  const [removingSongs, setRemovingSongs] = useState<Set<number>>(new Set());
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [isSliding, setIsSliding] = useState<boolean>(false);
  const [enteringSongs, setEnteringSongs] = useState<Set<number>>(new Set());
  const [slidingSongs, setSlidingSongs] = useState<Map<number, { fromX: number; fromY: number; toX: number; toY: number }>>(new Map());

  const songs = songData;

  const genres = useMemo((): string[] => {
    const uniqueGenres = Array.from(new Set(songs.map(song => song.genre))).sort();
    return uniqueGenres;
  }, [songs]);

  const getDecade = (year: number): string => {
    return `${Math.floor(year / 10) * 10}s`;
  };

  const decades = useMemo((): string[] => {
    const uniqueDecades = Array.from(new Set(songs.map(song => getDecade(song.year)))).sort((a, b) => {
      const decadeA = parseInt(a.replace('s', ''));
      const decadeB = parseInt(b.replace('s', ''));
      return decadeB - decadeA;
    });
    return uniqueDecades;
  }, [songs]);

  const filteredSongs = useMemo(() => {
    return songs.filter(song => {
      const matchesSearch = song.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           song.artist.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesGenre = selectedGenre === 'all' || song.genre === selectedGenre;
      const matchesDecade = selectedDecade === 'all' || getDecade(song.year) === selectedDecade;
      return matchesSearch && matchesGenre && matchesDecade;
    });
  }, [songs, searchTerm, selectedGenre, selectedDecade]);

  // Get actual DOM position of a song element
  const getElementPosition = (songId: number) => {
    const element = document.querySelector(`[data-song-id="${songId}"]`);
    if (element) {
      const rect = element.getBoundingClientRect();
      const container = document.querySelector('.song-list');
      if (container) {
        const containerRect = container.getBoundingClientRect();
        return {
          x: rect.left - containerRect.left,
          y: rect.top - containerRect.top
        };
      }
    }
    return { x: 0, y: 0 };
  };

  // Handle filter changes with animation
  const handleFilterChange = (newSearchTerm: string, newGenre: string, newDecade: string) => {
    const newFilteredSongs = songs.filter(song => {
      const matchesSearch = song.title.toLowerCase().includes(newSearchTerm.toLowerCase()) ||
                           song.artist.toLowerCase().includes(newSearchTerm.toLowerCase());
      const matchesGenre = newGenre === 'all' || song.genre === newGenre;
      const matchesDecade = newDecade === 'all' || getDecade(song.year) === newDecade;
      return matchesSearch && matchesGenre && matchesDecade;
    });

    // Find songs that are being removed and added
    const songsToRemove = displayedSongs.filter(song => 
      !newFilteredSongs.some(newSong => newSong.id === song.id)
    );
    const songsToAdd = newFilteredSongs.filter(song => 
      !displayedSongs.some(displayedSong => displayedSong.id === song.id)
    );

    if (songsToRemove.length > 0 || songsToAdd.length > 0) {
      setIsAnimating(true);
      
      // Capture current positions of ALL songs that will remain (including new ones)
      const currentPositions = new Map<number, { x: number; y: number }>();
      const remainingSongs = displayedSongs.filter(song => !songsToRemove.some(removed => removed.id === song.id));
      
      // For songs being added, we need to calculate where they would be in the current layout
      if (songsToAdd.length > 0) {
        // Temporarily add new songs to calculate their positions
        const tempSongList = [...remainingSongs, ...songsToAdd];
        songsToAdd.forEach(song => {
          const tempIndex = tempSongList.findIndex(s => s.id === song.id);
          const cols = 3;
          const row = Math.floor(tempIndex / cols);
          const col = tempIndex % cols;
          // Estimate position based on grid layout
          const estimatedX = col * (300 + 16); // Approximate item width + gap
          const estimatedY = row * (200 + 16); // Approximate item height + gap
          currentPositions.set(song.id, { x: estimatedX, y: estimatedY });
        });
      }
      
      // Capture positions of existing songs
      remainingSongs.forEach(song => {
        currentPositions.set(song.id, getElementPosition(song.id));
      });

      // Mark songs as removing
      const removingIds = new Set(songsToRemove.map(song => song.id));
      setRemovingSongs(removingIds);

      // After removal animation completes, update the displayed songs and trigger animations
      setTimeout(() => {
        // Update the displayed songs immediately
        setDisplayedSongs(newFilteredSongs);
        setRemovingSongs(new Set());
        
        // Calculate sliding animations for all songs (existing and new)
        if (songsToRemove.length > 0 || songsToAdd.length > 0) {
          // Use requestAnimationFrame to ensure DOM is updated
          requestAnimationFrame(() => {
            const slidingData = new Map<number, { fromX: number; fromY: number; toX: number; toY: number }>();
            const allSongsToAnimate = [...remainingSongs, ...songsToAdd];
            
            allSongsToAnimate.forEach(song => {
              const fromPos = currentPositions.get(song.id);
              const toPos = getElementPosition(song.id);
              
              if (fromPos && toPos && (fromPos.x !== toPos.x || fromPos.y !== toPos.y)) {
                slidingData.set(song.id, {
                  fromX: fromPos.x,
                  fromY: fromPos.y,
                  toX: toPos.x,
                  toY: toPos.y
                });
              }
            });
            
            if (slidingData.size > 0) {
              setSlidingSongs(slidingData);
              
              // Apply transforms immediately without transition
              allSongsToAnimate.forEach(song => {
                const slideData = slidingData.get(song.id);
                if (slideData) {
                  const element = document.querySelector(`[data-song-id="${song.id}"]`) as HTMLElement;
                  if (element) {
                    element.style.transition = 'none';
                    element.style.transform = `translate(${slideData.fromX - slideData.toX}px, ${slideData.fromY - slideData.toY}px)`;
                  }
                }
              });
              
              // Force reflow, then enable transitions and start animation
              requestAnimationFrame(() => {
                allSongsToAnimate.forEach(song => {
                  const slideData = slidingData.get(song.id);
                  if (slideData) {
                    const element = document.querySelector(`[data-song-id="${song.id}"]`) as HTMLElement;
                    if (element) {
                      element.style.transition = '';
                      element.style.transform = 'translate(0px, 0px)';
                    }
                  }
                });
                
                setIsSliding(true);
                
                setTimeout(() => {
                  setSlidingSongs(new Map());
                  setIsSliding(false);
                }, 500);
              });
            } else {
              // No sliding needed, animate in new songs immediately
              if (songsToAdd.length > 0) {
                const enteringIds = new Set(songsToAdd.map(song => song.id));
                setEnteringSongs(enteringIds);
                
                // Trigger entrance animation after a brief delay
                setTimeout(() => {
                  setEnteringSongs(new Set());
                }, 50);
              }
            }
          });
        } else {
          // No songs to remove, animate in new songs immediately
          if (songsToAdd.length > 0) {
            const enteringIds = new Set(songsToAdd.map(song => song.id));
            setEnteringSongs(enteringIds);
            
            // Trigger entrance animation after a brief delay
            setTimeout(() => {
              setEnteringSongs(new Set());
            }, 50);
          }
        }
        
        // End all animations - account for sliding animation
        const totalAnimationTime = (songsToRemove.length > 0 || songsToAdd.length > 0) ? 800 : 0; // 300ms removal + 500ms sliding
        setTimeout(() => {
          setIsAnimating(false);
        }, totalAnimationTime);
      }, 300); // Match the CSS transition duration for removal
    } else {
      // No changes needed, just update immediately
      setDisplayedSongs(newFilteredSongs);
    }
  };

  // Update displayed songs when filteredSongs changes (for initial load)
  React.useEffect(() => {
    if (!isAnimating && removingSongs.size === 0) {
      setDisplayedSongs(filteredSongs);
    }
  }, [filteredSongs, isAnimating, removingSongs.size]);

  // Trigger entrance animation after entering class is applied
  React.useEffect(() => {
    if (enteringSongs.size > 0) {
      const timer = setTimeout(() => {
        // Add animate-in class to trigger the entrance animation
        enteringSongs.forEach(songId => {
          const element = document.querySelector(`[data-song-id="${songId}"]`);
          if (element) {
            element.classList.add('animate-in');
          }
        });
      }, 10); // Small delay to ensure DOM is updated
      
      return () => clearTimeout(timer);
    }
  }, [enteringSongs]);

  // Reset transforms after sliding animation completes
  React.useEffect(() => {
    if (!isSliding && slidingSongs.size === 0) {
      // Reset all transforms
      const elements = document.querySelectorAll('.song-item');
      elements.forEach(element => {
        (element as HTMLElement).style.transform = '';
      });
    }
  }, [isSliding, slidingSongs.size]);

  const handleVideoClick = (song: Song) => {
    setSelectedVideo(song);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedVideo(null);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    handleFilterChange(newSearchTerm, selectedGenre, selectedDecade);
  };

  const handleGenreChange = (genre: string) => {
    setSelectedGenre(genre);
    handleFilterChange(searchTerm, genre, selectedDecade);
  };

  const handleDecadeChange = (decade: string) => {
    setSelectedDecade(decade);
    handleFilterChange(searchTerm, selectedGenre, decade);
  };

  return (
    <section className="content-section">
      <div className="container">
        <h1 className="section-title">Our Songlist</h1>
        <p style={{ textAlign: 'center', marginBottom: 'var(--spacing-xl)', fontSize: '1.2rem' }}>
          Here's a selection of songs we perform. From classic to modern hits.
        </p>
        
        <div className="search-container">
          <input 
            type="text" 
            id="song-search" 
            placeholder="Search songs or artists..." 
            className="search-input"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <i className="fas fa-search search-icon"></i>
        </div>
        
        {/* Song Counter */}
        <div className="song-counter">
          {displayedSongs.length === songs.length ? (
            <span>{songs.length} Songs</span>
          ) : (
            <span>{displayedSongs.length}/{songs.length} Songs</span>
          )}
        </div>
        
        <div className="songlist-layout">
          {/* Filters Sidebar */}
          <aside className="genre-sidebar">
            <h3 className="sidebar-title">Filter by Genre</h3>
            <div className="genre-filters">
              <button 
                className={`genre-btn ${selectedGenre === 'all' ? 'active' : ''}`}
                onClick={() => handleGenreChange('all')}
              >
                All Genres
              </button>
              {genres.map(genre => (
                <button 
                  key={genre}
                  className={`genre-btn ${selectedGenre === genre ? 'active' : ''}`}
                  onClick={() => handleGenreChange(genre)}
                >
                  {genre}
                </button>
              ))}
            </div>
            
            <h3 className="sidebar-title">Filter by Decade</h3>
            <div className="decade-filters">
              <button 
                className={`decade-btn ${selectedDecade === 'all' ? 'active' : ''}`}
                onClick={() => handleDecadeChange('all')}
              >
                All Decades
              </button>
              {decades.map(decade => (
                <button 
                  key={decade}
                  className={`decade-btn ${selectedDecade === decade ? 'active' : ''}`}
                  onClick={() => handleDecadeChange(decade)}
                >
                  {decade}
                </button>
              ))}
            </div>
          </aside>
          
          {/* Song List */}
          <div className="songlist-main">
            <ul className="song-list" id="song-list">
              {displayedSongs.map((song, index) => {
                const isEnteringThis = enteringSongs.has(song.id);
                const isSlidingThis = isSliding && slidingSongs.has(song.id);
                
                return (
                  <li 
                    key={song.id} 
                    className={`song-item ${removingSongs.has(song.id) ? 'removing' : ''} ${isAnimating ? 'animating' : ''} ${isSlidingThis ? 'sliding' : ''} ${isEnteringThis ? 'entering' : ''}`} 
                    data-genre={song.genre}
                    data-song-id={song.id}
                  >
                  <div className="song-title">{song.title}</div>
                  <div className="song-artist">by {song.artist}</div>
                  <div className="song-meta">
                    <span className="song-genre">{song.genre}</span>
                    <div className="song-year-container">
                      <span className="song-year">{song.year}</span>
                      {song.videoUrl && (
                        <button 
                          className="youtube-btn"
                          onClick={() => handleVideoClick(song)}
                          title="Watch on YouTube"
                        >
                          <i className="fab fa-youtube"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
      
      {/* Video Modal */}
      {isModalOpen && selectedVideo && (
        <div className="video-modal-overlay" onClick={closeModal}>
          <div className="video-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeModal}>
              ×
            </button>
            <h3 className="modal-title">{selectedVideo.title} - {selectedVideo.artist}</h3>
            <div className="modal-video-container">
              <iframe
                src={getEmbedUrl(selectedVideo.videoUrl!)}
                title={`${selectedVideo.title} - ${selectedVideo.artist}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="modal-video"
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Songlist;
